<template>
  <v-container class="py-12">
    <TitleHeader>Atas das Assembleias</TitleHeader>

    <v-row
      class="w-100 max-width mx-auto"
      align="start"
      justify="center"
      no-gutters
    >
      <v-col
        cols="12"
        sm="12"
        md="11"
        lg="10"
        xl="9"
      >
        <template v-if="loading">
          <div
            class="loading-state"
            aria-live="polite"
            role="status"
          >
            <div
              class="spinner"
              aria-hidden="true"
            ></div>
            <p class="loading-text">Carregando informações...</p>
          </div>
        </template>

        <template v-else-if="erro">
          <p class="status-message error">{{ erro }}</p>
        </template>

        <template v-else-if="atasPorAno.length">
          <section
            v-for="grupo in atasPorAno"
            :key="grupo.ano"
            class="ano-section"
            :aria-labelledby="`ano-${grupo.ano}`"
          >
            <h2
              :id="`ano-${grupo.ano}`"
              class="ano-titulo"
            >
              {{ grupo.ano }}
            </h2>

            <v-card
              elevation="6"
              class="pa-5 pa-sm-7 rounded-xl bg-form-card border-left-institucional"
            >
              <article
                v-for="(ata, index) in grupo.atas"
                :key="ata.id"
                class="conselho-section"
                :class="{ 'conselho-divider': index > 0 }"
              >
                <div class="conselho-cabecalho">
                  <v-icon
                    color="primary"
                    size="24"
                    aria-hidden="true"
                  >
                    mdi-account-group-outline
                  </v-icon>
                  <h3 class="conselho-titulo">{{ ata.conselho }}</h3>
                </div>

                <h4 class="ata-titulo">{{ ata.titulo }}</h4>

                <div
                  v-if="ata.descricao"
                  class="ata-descricao"
                  v-html="ata.descricao"
                ></div>

                <v-list
                  v-if="ata.arquivos && ata.arquivos.length"
                  density="compact"
                  class="arquivo-lista"
                >
                  <v-list-item
                    v-for="arquivo in arquivosOrdenados(ata.arquivos)"
                    :key="arquivo.id"
                    class="arquivo-item"
                  >
                    <v-list-item-title class="arquivo-titulo">
                      <a
                        :href="urlDownload(arquivo.id)"
                        target="_blank"
                        rel="noopener"
                        class="arquivo-link"
                      >
                        {{ formatarNomeArquivo(arquivo.nome) }}
                      </a>
                    </v-list-item-title>

                    <v-list-item-subtitle
                      v-if="arquivo.descricao"
                      class="arquivo-descricao"
                    >
                      {{ arquivo.descricao }}
                    </v-list-item-subtitle>
                  </v-list-item>
                </v-list>

                <p
                  v-else
                  class="sem-arquivos"
                >
                  Nenhuma ata publicada para este conselho.
                </p>
              </article>
            </v-card>
          </section>
        </template>

        <template v-else>
          <div class="empty-state">
            <div
              class="empty-icon"
              aria-hidden="true"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  d="M6 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6H6m7 1.5L18.5 9H13V3.5M8 13h8v2H8v-2m0 4h8v2H8v-2m0-8h5v2H8V9Z"
                />
              </svg>
            </div>
            <h2 class="empty-title">Nenhum resultado encontrado</h2>
            <p class="empty-description">Ainda não há registros disponíveis nesta seção.</p>
          </div>
        </template>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { formatarNomeArquivo } from '@/utils/arquivo'
import { computed, onMounted, ref } from 'vue'
import { useRuntimeConfig } from '#imports'
import { useRouter } from 'vue-router'
import TitleHeader from '@/components/TitleHeader.vue'

definePageMeta({
  middleware: 'manutencao',
  title: 'FUNARPEN - Atas das Assembleias',
})

useHead({
  title: 'Atas das Assembleias',
  meta: [{ name: 'description', content: 'Atas das Assembleias' }],
})

const config = useRuntimeConfig()
const baseUrl = config.public.backofficeApiBaseUrl
const router = useRouter()
const resultado = ref([])
const loading = ref(true)
const erro = ref('')

const ORDEM_CONSELHOS = {
  fiscal: 0,
  diretor: 1,
}

const atasPorAno = computed(() => {
  const grupos = new Map()

  resultado.value.forEach((ata) => {
    const dadosSlug = identificarAta(ata.slug)

    if (!dadosSlug) return

    if (!grupos.has(dadosSlug.ano)) {
      grupos.set(dadosSlug.ano, [])
    }

    grupos.get(dadosSlug.ano).push({
      ...ata,
      conselho: dadosSlug.conselho === 'fiscal' ? 'Conselho Fiscal' : 'Conselho Diretor',
      ordemConselho: ORDEM_CONSELHOS[dadosSlug.conselho],
    })
  })

  return Array.from(grupos, ([ano, atas]) => ({
    ano,
    atas: atas.sort((a, b) => a.ordemConselho - b.ordemConselho),
  })).sort((a, b) => Number(b.ano) - Number(a.ano))
})

function identificarAta(slug = '') {
  const correspondencia = slug.match(/conselho-(fiscal|diretor)-(\d{4})$/i)

  if (!correspondencia) return null

  return {
    conselho: correspondencia[1].toLowerCase(),
    ano: correspondencia[2],
  }
}

function urlDownload(id) {
  return `${baseUrl}/portal/arquivos/baixar/${id}`
}

function arquivosOrdenados(arquivos) {
  return [...arquivos].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))
}

async function buscarAtas() {
  loading.value = true
  erro.value = ''

  try {
    const subsecao = encodeURIComponent('Atas das Assembleias')
    const res = await fetch(`${baseUrl}/portal/paginas/buscar-subsecao?subsecao=${subsecao}`)

    if (res.status === 401) {
      router.push('/manutencao/acesso-negado')
      return
    }

    if (res.status === 404) {
      resultado.value = []
      return
    }

    if (!res.ok) throw new Error('Erro ao buscar dados')

    const data = await res.json()
    resultado.value = Array.isArray(data) ? data : []
  } catch (e) {
    erro.value = e.message.includes('Failed to fetch') ? 'Erro de conexão. Tente novamente mais tarde.' : e.message
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  buscarAtas()
})
</script>

<style scoped>
.max-width {
  max-width: 1200px;
  box-sizing: border-box;
}

.mx-auto {
  margin-left: auto;
  margin-right: auto;
}

.ano-section + .ano-section {
  margin-top: 2.75rem;
}

.ano-titulo {
  margin: 0 0 1rem;
  color: var(--funarpen-blue);
  font-size: clamp(1.75rem, 4vw, 2.25rem);
  font-weight: 800;
  line-height: 1.1;
}

.bg-form-card {
  background-color: rgba(255, 255, 255, 0.92);
}

.border-left-institucional {
  border-left: 6px solid var(--funarpen-blue);
}

.conselho-divider {
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(8, 47, 73, 0.14);
}

.conselho-cabecalho {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 0.75rem;
}

.conselho-titulo {
  margin: 0;
  color: var(--funarpen-blue);
  font-size: 1.35rem;
  font-weight: 700;
}

.ata-titulo {
  margin: 0 0 0.75rem;
  color: var(--funarpen-ink);
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.45;
}

.ata-descricao {
  padding: 1rem 1.25rem;
  background-color: rgba(11, 79, 108, 0.08);
  border-left: 4px solid var(--funarpen-gold);
  border-radius: 6px;
  color: var(--funarpen-ink);
  font-size: 0.95rem;
  line-height: 1.5;
}

.ata-descricao :deep(p) {
  margin: 0;
}

.arquivo-lista {
  margin-top: 1rem;
  background-color: transparent;
}

.arquivo-item {
  padding: 0.8rem 0;
  border-bottom: 1px dashed rgba(8, 47, 73, 0.14);
}

.arquivo-item:last-child {
  border-bottom: 0;
}

.arquivo-titulo {
  white-space: normal;
  word-break: break-word;
}

.arquivo-link {
  color: var(--funarpen-blue);
  text-decoration: none;
  font-weight: 600;
}

.arquivo-link:hover,
.arquivo-link:focus-visible {
  text-decoration: underline;
}

.arquivo-descricao {
  margin-top: 0.3rem;
  color: var(--funarpen-muted);
  font-size: 0.95rem;
  white-space: normal;
  word-break: break-word;
}

.sem-arquivos {
  margin: 1rem 0 0;
  color: var(--funarpen-muted);
  font-size: 0.95rem;
  font-style: italic;
}

.status-message {
  font-size: 1.2rem;
  text-align: center;
  color: var(--funarpen-red);
  font-weight: 600;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
  color: rgba(11, 69, 113, 0.8);
}

.spinner {
  width: 64px;
  height: 64px;
  border: 6px solid rgba(11, 69, 113, 0.2);
  border-top-color: rgba(11, 69, 113, 0.7);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1.5rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 1.25rem;
  font-weight: 500;
}

.empty-state {
  text-align: center;
  color: rgba(11, 69, 113, 0.85);
}

.empty-icon svg {
  width: 72px;
  height: 72px;
  fill: rgba(11, 69, 113, 0.6);
}

.empty-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
}

.empty-description {
  color: rgba(11, 69, 113, 0.75);
  font-size: 1.125rem;
  font-weight: 400;
  line-height: 1.6;
}
</style>
